"use client";

import { SafeHydrate } from "@/SafeHydrate";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Loader, Mail, Building2, Briefcase, Calendar, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const programOptions = [{
  value: "funding_deal",
  label: "Funding for a Deal",
  desc: "I need capital for a specific project"
}, {
  value: "jv_partnership",
  label: "JV Partnership",
  desc: "I want to partner on future deals"
}, {
  value: "referral_partner",
  label: "Referral Partner",
  desc: "I want to refer clients for commission"
}];
const fundingEngines = [{
  value: "Fix-and-Flip",
  label: "Fix-and-Flip",
  desc: "Short-term renovation capital",
  detail: "1st Mortgage + Gap"
}, {
  value: "BRRRR",
  label: "BRRRR",
  desc: "Buy, Rehab, Rent, Refinance",
  detail: "Bridge to Term"
}, {
  value: "Buy-and-Hold",
  label: "Buy-and-Hold",
  desc: "Long-term rental financing",
  detail: "DSCR 30-Year"
}, {
  value: "New-Construction",
  label: "New Construction",
  desc: "Ground-up development",
  detail: "Construction Debt"
}];
const experienceLevels = [{
  value: "0-2",
  label: "Emerging Investor",
  desc: "0-2 projects completed"
}, {
  value: "3-10",
  label: "Established Pro",
  desc: "3-10 projects completed"
}, {
  value: "10+",
  label: "Institutional Scale",
  desc: "10+ projects completed"
}];
const steps = [{
  num: "01",
  label: "PROGRAM"
}, {
  num: "02",
  label: "TRACK RECORD"
}, {
  num: "03",
  label: "ASSET SPECS"
}, {
  num: "04",
  label: "VERIFICATION"
}];
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    handleSubmit,
    control
  } = useWvcForm();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = React.useState(0);
  const [selectedPrograms, setSelectedPrograms] = React.useState<string[]>([]);
  const [direction, setDirection] = React.useState<"forward" | "back">("forward");
  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(undefined);
  const [confirmationCode] = React.useState(() => "BW-" + Math.random().toString(36).substring(2, 8).toUpperCase());
  const goNext = () => {
    setDirection("forward");
    setCurrentStep(s => Math.min(s + 1, steps.length - 1));
  };
  const goBack = () => {
    setDirection("back");
    setCurrentStep(s => Math.max(s - 1, 0));
  };
  const toggleProgram = (val: string) => {
    setSelectedPrograms(prev => prev.includes(val) ? prev.filter(p => p !== val) : [...prev, val]);
  };
  if (isSubmitted && !submitError) {
    setTimeout(() => {
      window.location.href = 'https://scheduler.zoom.us/monee-r/qualificationmeeting';
    }, 2000);
    return <div className="flex flex-col items-center justify-center min-h-[600px] text-center animate-in fade-in duration-700">
        <div className="mb-8 inline-flex items-center justify-center size-16 border border-yellow-500/30 rounded-lg">
          <Check className="size-8 text-yellow-500" />
        </div>
        <h2 className="font-default text-3xl md:text-4xl text-white mb-6 max-w-md leading-tight">
          Application Logged.
        </h2>
        <p className="font-mono text-xs tracking-widest text-slate-muted mb-3">
          TRANSACTION REFERENCE
        </p>
        <p className="font-mono text-base text-white mb-8 bg-slate-900 px-4 py-2 rounded-lg border border-slate-800">
          {confirmationCode}
        </p>
        <div className="h-px w-12 bg-yellow-500 mb-6" />
        <p className="font-mono text-xs tracking-widest text-slate-muted max-w-xs">
          OUR UNDERWRITING DESK WILL ANALYZE YOUR SUBMISSION WITHIN 24 BUSINESS HOURS.
        </p>
      </div>;
  }
  return <div className="w-full">
      <div className="mb-12 md:mb-16">
        <div className="flex items-center justify-between gap-2 md:gap-4">
          {steps.map((step, i) => {
          const isActive = i === currentStep;
          const isComplete = i < currentStep;
          return <React.Fragment key={step.num}>
                <div data-index={i} className="flex flex-col items-start gap-2 shrink-0">
                  <span className={`font-mono text-xs tracking-widest transition-colors duration-300 ${isActive ? "text-yellow-500" : isComplete ? "text-white" : "text-slate-muted/50"}`}>
                    {step.num}
                  </span>
                  <span className={`font-mono text-[10px] md:text-xs tracking-widest transition-colors duration-300 ${isActive ? "text-yellow-500" : isComplete ? "text-white" : "text-slate-muted/50"}`}>
                    {step.label}
                  </span>
                </div>
                {i < steps.length - 1 && <div className="flex-1 h-px bg-slate-muted/30 relative top-3">
                    <div className={`h-full bg-yellow-500 transition-all duration-500 ${i < currentStep ? "w-full" : "w-0"}`} />
                  </div>}
              </React.Fragment>;
        })}
        </div>
      </div>

      <Form onSubmit={handleSubmit}>
        <div className="relative overflow-hidden">
          <div key={currentStep} className={`${direction === "forward" ? "animate-in slide-in-from-right-8 fade-in" : "animate-in slide-in-from-left-8 fade-in"} duration-500`}>
            {currentStep === 0 && <div className="space-y-8">
                <div>
                  <p className="font-mono text-xs tracking-widest text-yellow-500 mb-4">
                    — PHASE 01: CAPITAL ALLOCATION
                  </p>
                  <h3 className="font-default text-2xl md:text-3xl text-white leading-tight">
                    What brings you to BeneWealth?
                  </h3>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  {programOptions.map((option, i) => <FormField key={option.value} control={control} name="programs" render={({
                field
              }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-lg border border-slate-muted/20 p-4 bg-slate-900">
                          <FormControl>
                            <Checkbox data-index={i} checked={selectedPrograms.includes(option.value)} onCheckedChange={checked => {
                    toggleProgram(option.value);
                    const current = field.value || [];
                    field.onChange(checked ? [...current, option.value] : current.filter((v: string) => v !== option.value));
                  }} className="mt-1 border-yellow-500 data-[state=checked]:bg-yellow-500 data-[state=checked]:text-slate-950" />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel className="text-lg font-default text-white">
                              {option.label}
                            </FormLabel>
                            <p className="text-xs font-mono text-slate-muted tracking-widest">
                              {option.desc.toUpperCase()}
                            </p>
                          </div>
                        </FormItem>} />)}
                </div>

                {selectedPrograms.includes("funding_deal") && <div className="pt-6 border-t border-slate-muted/20 animate-in fade-in slide-in-from-top-4">
                    <p className="font-mono text-xs tracking-widest text-yellow-500 mb-4">SELECT FUNDING ENGINE</p>
                    <FormField control={control} name="loan_type" render={({
                field
              }) => <FormItem>
                          <FormControl>
                            <RadioGroup value={field.value} onValueChange={field.onChange} className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {fundingEngines.map((loan, i) => <label data-index={i} key={loan.value} htmlFor={`loan-${i}`} className={`group cursor-pointer border border-slate-muted/20 p-5 rounded-lg transition-all duration-200 hover:border-yellow-500/60 ${field.value === loan.value ? "border-yellow-500 bg-yellow-500/5" : "bg-slate-900"}`}>
                                  <div className="flex items-start justify-between gap-3">
                                    <div>
                                      <p className="font-default text-lg text-white mb-1">
                                        {loan.label}
                                      </p>
                                      <p className="font-mono text-[10px] tracking-widest text-slate-muted">
                                        {loan.desc}
                                      </p>
                                    </div>
                                    <span className="font-mono text-[10px] text-yellow-500 border border-yellow-500/30 px-2 py-0.5 rounded-lg">
                                      {loan.detail}
                                    </span>
                                  </div>
                                  <RadioGroupItem value={loan.value} id={`loan-${i}`} className="sr-only" />
                                </label>)}
                            </RadioGroup>
                          </FormControl>
                        </FormItem>} />
                  </div>}

                {selectedPrograms.includes("jv_partnership") && <div className="pt-6 border-t border-slate-muted/20 animate-in fade-in slide-in-from-top-4 space-y-4">
                    <p className="font-mono text-xs tracking-widest text-yellow-500 mb-4">JV QUALIFICATIONS</p>
                    <div className="grid grid-cols-1 gap-3">
                      <FormField control={control} name="jv_credit_score" render={({
                  field
                }) => <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                            <FormControl>
                              <Checkbox checked={field.value} onCheckedChange={field.onChange} className="border-yellow-500" />
                            </FormControl>
                            <FormLabel className="text-sm font-mono text-white">CREDIT SCORE 700+</FormLabel>
                          </FormItem>} />
                      <FormField control={control} name="jv_capital" render={({
                  field
                }) => <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                            <FormControl>
                              <Checkbox checked={field.value} onCheckedChange={field.onChange} className="border-yellow-500" />
                            </FormControl>
                            <FormLabel className="text-sm font-mono text-white">AVAILABLE CAPITAL FOR INVESTMENT</FormLabel>
                          </FormItem>} />
                      <FormField control={control} name="jv_experience" render={({
                  field
                }) => <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                            <FormControl>
                              <Checkbox checked={field.value} onCheckedChange={field.onChange} className="border-yellow-500" />
                            </FormControl>
                            <FormLabel className="text-sm font-mono text-white">REAL ESTATE INVESTING EXPERIENCE</FormLabel>
                          </FormItem>} />
                    </div>
                  </div>}

                {selectedPrograms.includes("referral_partner") && <div className="pt-6 border-t border-slate-muted/20 animate-in fade-in slide-in-from-top-4 space-y-6">
                    <p className="font-mono text-xs tracking-widest text-yellow-500 mb-4">REFERRAL NETWORK DETAILS</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField control={control} name="referral_source" render={({
                  field
                }) => <FormItem>
                            <FormLabel className="font-mono text-[10px] tracking-widest text-slate-muted">SOURCE TYPE (BROKER, REALTOR, ETC)</FormLabel>
                            <FormControl>
                              <Input {...field} className="bg-slate-900 border-slate-800 rounded-lg" />
                            </FormControl>
                          </FormItem>} />
                      <FormField control={control} name="referral_license" render={({
                  field
                }) => <FormItem>
                            <FormLabel className="font-mono text-[10px] tracking-widest text-slate-muted">LICENSE NUMBER (IF APPLICABLE)</FormLabel>
                            <FormControl>
                              <Input {...field} className="bg-slate-900 border-slate-800 rounded-lg" />
                            </FormControl>
                          </FormItem>} />
                      <FormField control={control} name="referral_volume" render={({
                  field
                }) => <FormItem className="md:col-span-2">
                            <FormLabel className="font-mono text-[10px] tracking-widest text-slate-muted">ESTIMATED MONTHLY REFERRAL VOLUME</FormLabel>
                            <FormControl>
                              <Input {...field} className="bg-slate-900 border-slate-800 rounded-lg" />
                            </FormControl>
                          </FormItem>} />
                    </div>
                  </div>}
              </div>}

            {currentStep === 1 && <div className="space-y-8">
                <div>
                  <p className="font-mono text-xs tracking-widest text-yellow-500 mb-4">
                    — PHASE 02: TRACK RECORD
                  </p>
                  <h3 className="font-default text-2xl md:text-3xl text-white leading-tight">
                    Define your investment experience.
                  </h3>
                </div>

                {selectedPrograms.includes("funding_deal") && <FormField control={control} name="experience_level" rules={{
              required: selectedPrograms.includes("funding_deal") ? "Please select your level" : false
            }} render={({
              field
            }) => <FormItem>
                        <FormControl>
                          <RadioGroup value={field.value} onValueChange={field.onChange} className="grid grid-cols-1 gap-3">
                            {experienceLevels.map((level, i) => <label data-index={i} key={level.value} htmlFor={`exp-${i}`} className={`group cursor-pointer border border-slate-muted/20 p-5 rounded-lg transition-all duration-200 hover:border-yellow-500/60 ${field.value === level.value ? "border-yellow-500 bg-yellow-500/5" : "bg-slate-900"}`}>
                                <div className="flex items-center justify-between gap-4">
                                  <div className="flex items-center gap-4">
                                    <div className={`p-2 rounded-lg border ${field.value === level.value ? 'border-yellow-500/40 text-yellow-500' : 'border-slate-800 text-slate-muted'}`}>
                                      <Briefcase className="size-5" />
                                    </div>
                                    <div>
                                      <p className="font-default text-lg text-white mb-1">
                                        {level.label}
                                      </p>
                                      <p className="font-mono text-[10px] tracking-widest text-slate-muted">
                                        {level.desc.toUpperCase()}
                                      </p>
                                    </div>
                                  </div>
                                  <span className={`font-mono text-xs ${field.value === level.value ? "text-yellow-500" : "text-slate-muted/50"}`}>
                                    0{i + 1}
                                  </span>
                                </div>
                                <RadioGroupItem value={level.value} id={`exp-${i}`} className="sr-only" />
                              </label>)}
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />}

                {selectedPrograms.includes("jv_partnership") && <div className="p-6 border border-slate-800 bg-slate-900/50 rounded-lg">
                    <p className="font-mono text-[10px] tracking-widest text-yellow-500 mb-4">JV PARTNERSHIP CONFIRMATION</p>
                    <p className="text-sm text-slate-muted">You have indicated interest in our JV Partnership program. Our team will review your qualifications during the initial consultation.</p>
                  </div>}

                {selectedPrograms.includes("referral_partner") && <div className="p-6 border border-slate-800 bg-slate-900/50 rounded-lg">
                    <p className="font-mono text-[10px] tracking-widest text-yellow-500 mb-4">REFERRAL NETWORK CONFIRMATION</p>
                    <p className="text-sm text-slate-muted">You are applying as a Referral Partner. We will verify your professional credentials and discuss commission structures.</p>
                  </div>}
              </div>}

            {currentStep === 2 && <div className="space-y-10">
                <div>
                  <p className="font-mono text-xs tracking-widest text-yellow-500 mb-4">
                    — PHASE 03: ASSET SPECS
                  </p>
                  <h3 className="font-default text-2xl md:text-3xl text-white leading-tight">
                    Project logistics and timeline.
                  </h3>
                </div>

                {selectedPrograms.includes("funding_deal") ? <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <FormField control={control} name="closing_date" render={({
                field
              }) => <FormItem>
                          <FormLabel className="font-mono text-xs tracking-widest text-slate-muted flex items-center gap-2">
                            <SafeHydrate><Calendar className="size-3" /></SafeHydrate>
                            TARGET CLOSING DATE
                          </FormLabel>
                          <FormControl>
                            <div className="border border-slate-muted/30 p-2 inline-block bg-slate-900 rounded-lg">
                              <SafeHydrate><CalendarComponent mode="single" selected={selectedDate} onSelect={date => {
                        setSelectedDate(date);
                        field.onChange(date ? date.toISOString().split("T")[0] : "");
                      }} className="rounded-lg" /></SafeHydrate>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>} />

                    <div className="space-y-8">
                      <FormField control={control} name="project_details" render={({
                  field
                }) => <FormItem>
                            <FormLabel className="font-mono text-xs tracking-widest text-slate-muted flex items-center gap-2">
                              <Building2 className="size-3" />
                              PROJECT SCOPE & STRATEGY
                            </FormLabel>
                            <FormControl>
                              <Textarea {...field} placeholder="Describe the property, purchase price, and renovation budget." className="border-0 border-b border-slate-muted bg-transparent px-0 text-lg font-default text-white placeholder:text-slate-muted/40 focus-visible:ring-0 focus-visible:border-yellow-500 min-h-32 resize-none rounded-lg" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>} />
                    </div>
                  </div> : <div className="p-12 border border-dashed border-slate-800 rounded-lg text-center">
                    <p className="font-mono text-xs text-slate-muted tracking-widest">NO SPECIFIC ASSET SPECS REQUIRED FOR SELECTED PROGRAMS</p>
                    <p className="text-sm text-slate-muted mt-2">Proceed to final verification.</p>
                  </div>}
              </div>}

            {currentStep === 3 && <div className="space-y-10">
                <div>
                  <p className="font-mono text-xs tracking-widest text-yellow-500 mb-4">
                    — PHASE 04: VERIFICATION
                  </p>
                  <h3 className="font-default text-2xl md:text-3xl text-white leading-tight">
                    Finalize your application.
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-10">
                  <FormField control={control} name="full_name" rules={{
                required: "Name is required"
              }} render={({
                field
              }) => <FormItem>
                        <FormLabel className="font-mono text-xs tracking-widest text-slate-muted flex items-center gap-2">
                          <User className="size-3" />
                          FULL LEGAL NAME
                        </FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="John Investor" className="border-0 border-b border-slate-muted bg-transparent px-0 text-xl font-default text-white h-14 placeholder:text-slate-muted/40 focus-visible:ring-0 focus-visible:border-yellow-500 rounded-lg" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />

                  <FormField control={control} name="email_address" rules={{
                required: "Email is required"
              }} render={({
                field
              }) => <FormItem>
                        <FormLabel className="font-mono text-xs tracking-widest text-slate-muted flex items-center gap-2">
                          <Mail className="size-3" />
                          BUSINESS EMAIL
                        </FormLabel>
                        <FormControl>
                          <Input {...field} type="email" placeholder="john@investor.com" className="border-0 border-b border-slate-muted bg-transparent px-0 text-xl font-default text-white h-14 placeholder:text-slate-muted/40 focus-visible:ring-0 focus-visible:border-yellow-500 rounded-lg" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />

                  <FormField control={control} name="phone_number" rules={{
                required: "Phone is required"
              }} render={({
                field
              }) => <FormItem className="md:col-span-2">
                        <FormLabel className="font-mono text-xs tracking-widest text-slate-muted">
                          DIRECT PHONE NUMBER
                        </FormLabel>
                        <FormControl>
                          <Input {...field} type="tel" placeholder="+1 555-000-0000" className="border-0 border-b border-slate-muted bg-transparent px-0 text-xl font-default text-white h-14 placeholder:text-slate-muted/40 focus-visible:ring-0 focus-visible:border-yellow-500 rounded-lg" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                </div>
              </div>}
          </div>
        </div>

        {submitError && <div className="mt-8 p-4 border border-red-500/40 bg-red-950/20 rounded-lg">
            <p className="font-mono text-xs text-red-400">{submitError}</p>
          </div>}

        <div className="flex items-center justify-between mt-16 pt-8 border-t border-slate-muted/30">
          <Button type="button" variant="ghost" onClick={goBack} disabled={currentStep === 0} className="font-mono text-xs tracking-widest text-slate-muted hover:text-white hover:bg-transparent px-0 disabled:opacity-30 rounded-lg">
            <ArrowLeft className="size-3 mr-2" />
            BACK
          </Button>

          {currentStep < steps.length - 1 ? <Button type="button" onClick={goNext} className="px-8 py-6 font-mono text-xs tracking-widest bg-yellow-500 text-slate-950 hover:bg-yellow-400 rounded-lg shadow-lg">
              CONTINUE
              <ArrowRight className="size-3 ml-2" />
            </Button> : <Button type="submit" disabled={isSubmitting} className="px-8 py-6 font-mono text-xs tracking-widest bg-yellow-500 text-slate-950 hover:bg-yellow-400 rounded-lg shadow-lg">
              {isSubmitting ? <>
                  <Loader className="size-3 mr-2 animate-spin" />
                  PROCESSING
                </> : <>
                  SUBMIT APPLICATION
                  <ArrowRight className="size-3 ml-2" />
                </>}
            </Button>}
        </div>
      </Form>
    </div>;
}
export default function PreQualificationForm() {
  return <section data-section-id="3839" id="pre-qualification-form" className="bg-slate-950 text-white relative overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-12 min-h-screen">
        <div className="lg:col-span-5 relative lg:sticky lg:top-0 lg:h-screen overflow-hidden">
          <div className="absolute inset-0">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722377/img/vault_blueprint_architecture.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722377/img/vault_blueprint_architecture.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722377/img/vault_blueprint_architecture.webp-wvc-sizes" alt="Architectural blueprint of a real estate development" className="w-full h-full object-cover opacity-60" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-b from-slate-950/40 via-slate-950/20 to-slate-950/90" />
          </div>

          <div className="relative h-full flex flex-col justify-between p-8 md:p-12 lg:p-16">
            <div>
              <p className="font-mono text-xs tracking-widest text-yellow-500 mb-8">
                — ARCHITECT OF CAPITAL
              </p>
              <h1 className="font-default text-4xl md:text-5xl lg:text-6xl text-white leading-[1.05] tracking-tight">
                Fund the
                <br />
                whole deal.
              </h1>
            </div>

            <div className="mt-12 lg:mt-0 max-w-xs">
              <div className="h-px w-12 bg-yellow-500 mb-6" />
              <p className="font-default text-lg text-white mb-1">
                BeneWealth Financial
              </p>
              <p className="font-mono text-xs tracking-widest text-slate-muted mb-4 flex items-center gap-2">
                <Mail className="size-3" />
                support@benewealth.info
              </p>
              <p className="font-mono text-[10px] tracking-widest text-slate-muted">
                RESPONSE TIME — 24 HOURS
              </p>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7 bg-slate-950 px-6 py-16 md:px-12 md:py-20 lg:px-24 lg:py-32">
          <div className="max-w-2xl mx-auto">
            <FormProvider formKey="pre_qualification_form_pre_qualification_form" formId="pre_qualification_form_pre_qualification_form" sectionName="pre-qualification-form" defaultValues={{
            full_name: "",
            email_address: "",
            phone_number: "",
            loan_type: "",
            closing_date: "",
            experience_level: "",
            project_details: "",
            programs: [],
            jv_credit_score: false,
            jv_capital: false,
            jv_experience: false,
            referral_source: "",
            referral_license: "",
            referral_volume: ""
          }} validationRules={{
            full_name: {
              required: true
            },
            email_address: {
              required: true,
              pattern: "email"
            },
            phone_number: {
              required: true,
              pattern: "phone"
            }
          }} submitText="Submit Application" successMessage="Application received. Our underwriting team will review within 24 hours." errorMessage="Submission failed. Please verify your details and try again.">
              <InquiryForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}